import React from "react";
import { Check, RefreshCw, ArrowLeft, AlertCircle } from "lucide-react";
import { Spinner } from "../constants";
import { SX, FRAME } from "./studioTokens";

/* The five stages the prototype shows are the backend's real BuildStatus stages,
   so this is driven by the polled status rather than a timer. */
const STAGES = [
  { key: "understanding", label: "Reading document structure", sub: "Reading document with AI vision…" },
  { key: "indexing", label: "Indexing into search engine", sub: "Indexing content for retrieval…" },
  { key: "generating", label: "Generating fields & instructions", sub: "Inferring fields and writing instructions…" },
  { key: "validating", label: "Validating schema", sub: "Checking field types and consistency…" },
  { key: "testing", label: "Running test extraction", sub: "Extracting values from your sample…" },
];
const ORDER = STAGES.map((s) => s.key);

const GeneratingProgress = ({ buildStatus, onRetry, onCancel }) => {
  const { status, stage, stage_label, error } = buildStatus || {};
  const isError = status === "error";
  const isReady = status === "ready";
  const idx = ORDER.indexOf(stage);
  const active = isReady ? STAGES.length : idx < 0 ? 0 : idx;
  const pct = Math.round((active / STAGES.length) * 100);
  const currentSub = isReady ? "Finishing up…" : (stage_label || STAGES[active]?.sub || "Starting…");

  return (
    <div className={`flex-1 overflow-auto ${FRAME.canvas} px-6 py-8`}>
      <div className="max-w-[620px] mx-auto text-left">
        <div className={`text-[24px] font-semibold tracking-[-0.2px] ${isError ? "text-[#B33A1E]" : "text-[#1A1A1A]"}`}>
          {isError ? "Couldn't build your template" : "Building your template…"}
        </div>
        <div className="text-[14px] text-[#52525B] mt-2 leading-[1.5]">
          {isError
            ? "The build stopped before it finished. You can retry, or go back and try a different document."
            : "Analysing structure, inferring fields, writing instructions, and running a test extraction."}
        </div>

        {!isError && (
          <>
            <div className="h-[5px] rounded-full bg-[#F5F5F5] overflow-hidden my-[22px]">
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.max(pct, 6)}%`, background: "linear-gradient(90deg, #A04A97, #893F82)" }} />
            </div>
            <div className="flex items-center gap-2.5 text-[13.5px] text-[#52525B] mb-5">
              <Spinner size={15} /> {currentSub}
            </div>
          </>
        )}

        {isError && (
          <div className="flex items-start gap-2 my-5 px-3.5 py-3 rounded-[10px] bg-[#FBE9E5] border border-[#F3D6D6] text-[13px] text-[#B33A1E] leading-[1.5]">
            <AlertCircle size={15} className="mt-px flex-shrink-0" /> {error || "Build failed."}
          </div>
        )}

        <div className={`${SX.card} overflow-hidden`}>
          {STAGES.map((s, i) => {
            const done = i < active;
            const isActive = i === active && !isError;
            return (
              <div key={s.key}
                className={`flex items-center gap-3.5 px-[18px] py-[15px] border-b border-[#ECE9EE] last:border-b-0 ${isActive ? "bg-[#FBF5FA]" : "bg-white"}`}>
                <span className={`w-[26px] h-[26px] rounded-full flex-shrink-0 flex items-center justify-center text-[12px] font-semibold ${
                  done ? "bg-[#2F7D44] text-white"
                    : isActive ? "bg-white text-[#A04A97] border-2 border-[#A04A97]"
                    : "bg-white text-[#8A8A90] border-[1.5px] border-[#ECE9EE]"
                }`}>
                  {done ? <Check size={13} /> : isActive ? <Spinner size={14} /> : i + 1}
                </span>
                <span className={`text-[14px] ${isActive ? "font-semibold text-[#1A1A1A]" : done ? "font-medium text-[#1A1A1A]" : "font-medium text-[#8A8A90]"}`}>{s.label}</span>
              </div>
            );
          })}
        </div>

        {isError && (
          <div className="flex items-center gap-2.5 mt-5">
            <button onClick={onRetry} className={`${SX.btn} ${SX.btnPrimary}`}><RefreshCw size={14} /> Retry build</button>
            <button onClick={onCancel} className={`${SX.btn} ${SX.btnGhost}`}><ArrowLeft size={14} /> Start over</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneratingProgress;
